import { ENV } from '@/config';
import { useEffect, useState } from 'react';

import { reportHandledError, reportMediaError } from '@/lib/errorReporting';

interface ImageBlockProps {
  src: string;
  alt: string;
  objectFit: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  backgroundColor: string;
  rotation?: number;
  paddingTop?: number;
  paddingRight?: number;
  paddingBottom?: number;
  paddingLeft?: number;
}

function ImageBlock(props: ImageBlockProps) {
  const [imgSrc, setImgSrc] = useState(props.src); // [1
  const { src, alt, objectFit = 'cover', backgroundColor = '#fff', paddingTop = 0, paddingRight = 0, paddingBottom = 0, paddingLeft = 0 } = props;

  useEffect(() => {
    async function getImageFromCache(src: string) {
      try {
        if (ENV === 'debug') {
          setImgSrc(src);
          return;
        }

        // Check if the image URL is cached in localStorage
        const cachedImgSrc = localStorage.getItem(src);
        if (cachedImgSrc) {
          console.log('Image found in cache');
          setImgSrc(cachedImgSrc);
        } else {
          console.log('Image not found in cache');
          localStorage.setItem(src, src); // Cache the image URL
          setImgSrc(src);
          // Handle the case when the image is not found in cache
          // You might want to fetch the image from the network here
          // and cache it in localStorage for future use
        }
      } catch (error) {
        // Do NOT touch localStorage again here. The usual cause of landing in
        // this catch is QuotaExceededError, so retrying setItem throws a second
        // time — out of an async function, as an unhandled rejection.
        console.error('Error getting image from cache', error);
        reportHandledError(error, { where: 'ImageBlock.getImageFromCache', src });
        setImgSrc(src); // Fall back to the original image URL — display still works.
      }
    }
    getImageFromCache(src);
  }, [src]); // Execute useEffect when src changes

  return (
    <div className="w-full h-full" style={{ paddingTop, paddingRight, paddingBottom, paddingLeft, boxSizing: 'border-box' }}>
      <img
        className="w-full h-full object-cover"
        src={imgSrc ?? src}
        alt={alt}
        // Like <video>, a failed image raises an event rather than throwing,
        // so without this the page just renders a gap and nobody finds out.
        onError={() => reportMediaError('image', imgSrc ?? src)}
        style={{
          objectFit,
          backgroundColor,
        }}
      />
    </div>
  );
}

export default ImageBlock;
